import React, { useState, useEffect, useContext } from 'react';
import AccountStyles from './Account.styled';
import {
  Stack,
  TextInput,
  Typography,
  ContactCard,
  Checkbox,
} from 'dx-sdk/build/components';
import { Address, Phone } from 'dx-sdk/build/icons';
import { Theme as ThemeProvider } from 'dx-sdk/build/providers';
import Loading from '../../../images/svg/icons/Loading';
import {
  formatDealerName,
  formatPhoneNumberFromNumber,
} from 'pubweb-smokey/dist/utils/formatters';
import { DXAccountContext } from '../../../contexts/DXAccountContext';
import { useLocation } from 'react-router-dom';

export const Account = ({ dealer }) => {
  const dxContext = useContext(DXAccountContext);
  const [numberError, setNumberError] = useState(false);
  const [numberNote, setNumberNote] = useState();
  const [zipcodeError, setZipcodeError] = useState(false);
  const [zipcodeNote, setZipcodeNote] = useState();
  const [saveTimeout, setSaveTimeout] = useState(0);
  const [updatedUser, setUpdatedUser] = useState({
    userId: null,
    firstName: '',
    lastName: '',
    phone: '',
    zipcode: '',
    preferredPronouns: '',
    preferredContactMethod: [],
    preferredContactTime: [],
  });

  const location = useLocation();

  const params = new URLSearchParams(location.search);

  const saveUserChanges = (userUpdate) => {
    if (saveTimeout) {
      window.clearTimeout(saveTimeout);
      setSaveTimeout(0);
    }

    setSaveTimeout(
      window.setTimeout(() => {
        dxContext?.actions?.updateAccountRetail(userUpdate);
        setSaveTimeout(0);
      }, 1000)
    );
  };

  function formatPhoneNumber(value) {
    if (!value) return value;
    const phoneNumber = value.replace(/[^\d]/g, '');
    const phoneNumberLength = phoneNumber.length;

    if (phoneNumberLength < 4) return phoneNumber;
    if (phoneNumberLength < 7) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    }
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(
      3,
      6
    )}-${phoneNumber.slice(6, 10)}`;
  }

  const phoneFormatRegex =
    /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im;
  const validZipRegex = /(^\d{5}$)|(^\d{5}-\d{4}$)/;
  const numbersOnlyRegex = /^-?\d+\.?\d*$/;

  const handleError = (key) => (value) => {
    const userUpdateBlur = {
      ...updatedUser,
      [key]: value,
    };

    if (key === 'phone') {
      if (phoneFormatRegex.test(value) || value == '') {
        setNumberError(false);
        setNumberNote('');
        saveUserChanges(userUpdateBlur);
      } else {
        setNumberError(true);
        setNumberNote('Please enter a valid 10 digit number');
      }
    } else if (key === 'zipcode') {
      if (validZipRegex.test(value) || value == '') {
        setZipcodeError(false);
        setZipcodeNote('');
        saveUserChanges(userUpdateBlur);
      } else {
        setZipcodeError(true);
        setZipcodeNote('Please enter valid Zip Code');
      }
    } else {
      return null;
    }
  };

  //  const handlePhoneChange = (v) => {
  //    const formattedPhoneNumber = formatPhoneNumber(v);
  //    setPhone(formattedPhoneNumber);
  //  };

  const handleTextChange = (key) => (value) => {
    const userUpdateText = {
      ...updatedUser,
      [key]: key === 'phone' ? formatPhoneNumber(value) : value,
    };

    if (key === 'zipcode' && validZipRegex.test(value)) {
      setZipcodeError(false);
      setZipcodeNote('');
    }

    if ((key !== 'phone' && key !== 'zipcode') || value === '') {
      setUpdatedUser(userUpdateText);
      saveUserChanges(userUpdateText);
    } else if (key === 'zipcode' && numbersOnlyRegex.test(value)) {
      setUpdatedUser(userUpdateText);
    } else if (key === 'phone') {
      setUpdatedUser(userUpdateText);
    } else {
      setUpdatedUser(userUpdateText);
      saveUserChanges(userUpdateText);
    }
  };

  const handleRadioChange = (key, value) => () => {
    //  const values = updatedUser[key];
    //  const hasValue = values.includes(value);
    //  const newValues = hasValue
    //    ? values.filter((val) => val !== value)
    //    : [...values, value];
    const userUpdate = {
      ...updatedUser,
      [key]: value,
    };

    setUpdatedUser(userUpdate);
    saveUserChanges(userUpdate);
  };

  const handleCheckChange = (key, value) => () => {
    const values = updatedUser[key];
    const hasValue = values.includes(value);
    const newValues = hasValue
      ? values.filter((val) => val !== value)
      : [...values, value];
    const userUpdate = {
      ...updatedUser,
      [key]: newValues,
    };

    setUpdatedUser(userUpdate);
    saveUserChanges(userUpdate);
  };

  useEffect(() => {
    if (dxContext?.state?.account && !updatedUser.userId) {
      setUpdatedUser({
        userId: dxContext?.state?.account.userId,
        firstName: dxContext?.state?.account.firstName,
        lastName: dxContext?.state?.account.lastName,
        phone: dxContext?.state?.account.phone,
        zipcode: dxContext?.state?.account.zipcode,
        preferredPronouns: dxContext?.state?.account.preferredPronouns,
        preferredContactMethod:
          dxContext?.state?.account.preferredContactMethod,
        preferredContactTime: dxContext?.state?.account.preferredContactTime,
      });
    }
  }, [dxContext?.state?.account]);

  return (
    <>
      <ThemeProvider theme={{}}>
        <AccountStyles>
          {dxContext?.state?.account ? (
            <div className="container">
              <Typography variant="h1" component="h1" className="headline">
                Account Details
              </Typography>

              <Stack
                className="account-wrap"
                direction={{ small: 'column', large: 'row' }}
                justifyContent="flex-start"
                alignItems="stretch"
              >
                <div className="account-left">
                  <Typography
                    variant="h4"
                    component="span"
                    className="account-input-header"
                  >
                    Personal Information
                  </Typography>
                  {params.get('userId') && (
                    <p>User ID: {updatedUser?.userId}</p>
                  )}
                  <div className="flex-half">
                    <TextInput
                      label="First Name"
                      color="#4E5255"
                      value={updatedUser?.firstName}
                      variant="flat"
                      fullwidth
                      id="firstName"
                      handlers={{
                        onChange: handleTextChange('firstName'),
                      }}
                      componentsProps={{
                        Input: {
                          maxlength: '40',
                        },
                      }}
                    />
                    <TextInput
                      label="Last Name"
                      color="#4E5255"
                      value={updatedUser?.lastName}
                      variant="flat"
                      fullwidth
                      id="lastName"
                      handlers={{
                        onChange: handleTextChange('lastName'),
                      }}
                      componentsProps={{
                        Input: {
                          maxlength: '50',
                        },
                      }}
                    />
                  </div>
                  <div className="flex-half">
                    {/* <TextInput
                      label="Email"
                       color="#4E5255"
                      value={updatedUser?.email}
                      variant="flat"
                      fullwidth
                      id="email"
                      handlers={{
                        onChange: handleTextChange('email'),
                      }}
                    /> */}
                    <TextInput
                      label="Phone Number"
                      color="#4E5255"
                      value={updatedUser?.phone}
                      variant="flat"
                      fullwidth
                      id="phone"
                      note={numberNote}
                      error={numberError}
                      handlers={{
                        onChange: handleTextChange('phone'),
                        onBlur: handleError('phone'),
                      }}
                    />
                    <TextInput
                      label="Zip Code"
                      color="#4E5255"
                      value={updatedUser.zipcode}
                      variant="flat"
                      fullwidth
                      id="zipcode"
                      note={zipcodeNote}
                      error={zipcodeError}
                      handlers={{
                        onChange: handleTextChange('zipcode'),
                        onBlur: handleError('zipcode'),
                      }}
                    />
                  </div>
                  {/* <div className="flex-half">
                    <TextInput
                      label="Preferred Pronouns"
                      color="#4E5255"
                      value={updatedUser.preferredPronouns}
                      variant="flat"
                      fullwidth
                      id="preferredPronouns"
                      handlers={{
                        onChange: handleTextChange('preferredPronouns'),
                      }}
                      values={[
                        {
                          label: 'They/Them',
                          value: 'They/Them',
                        },
                        {
                          label: 'She/Her',
                          value: 'She/Her',
                        },
                        {
                          label: 'He/Him',
                          value: 'He/Him',
                        },
                      ]}
                    />
                  </div> */}
                  <Stack
                    alignItems="flex-start"
                    gap="0px"
                    className="notification-settings-stack"
                  >
                    <Typography
                      variant="h4"
                      component="span"
                      className="account-input-header"
                    >
                      Notification Settings
                    </Typography>
                    <Stack alignItems="flex-start">
                      <Typography variant="intro">
                        Preferred Contact Method
                      </Typography>
                      <Stack direction="row" className="preferred-method-list">
                        <Checkbox
                          id="Call"
                          label="Call"
                          checked={updatedUser.preferredContactMethod?.includes(
                            'Call'
                          )}
                          handlers={{
                            onChange: handleRadioChange(
                              'preferredContactMethod',
                              'Call'
                            ),
                          }}
                        />
                        <Checkbox
                          id="Text"
                          label="Text"
                          checked={updatedUser.preferredContactMethod?.includes(
                            'Text'
                          )}
                          handlers={{
                            onChange: handleRadioChange(
                              'preferredContactMethod',
                              'Text'
                            ),
                          }}
                        />
                        <Checkbox
                          id="Email"
                          label="Email"
                          checked={updatedUser.preferredContactMethod?.includes(
                            'Email'
                          )}
                          handlers={{
                            onChange: handleRadioChange(
                              'preferredContactMethod',
                              'Email'
                            ),
                          }}
                        />
                      </Stack>
                    </Stack>
                  </Stack>
                </div>
                <div className="account-right">
                  <ContactCard
                    heading="Your Home Center"
                    subheading={formatDealerName(dealer?.dealerName)}
                    componentsProps={{
                      Heading: {
                        variant: 'h5',
                        component: 'h5',
                      },
                      Item: {
                        component: 'span',
                      },
                    }}
                    items={[
                      {
                        label: (
                          <a
                            className="phone"
                            href={'tel:' + dealer?.phoneNumber}
                          >
                            {formatPhoneNumberFromNumber(dealer?.phoneNumber)}
                          </a>
                        ),
                        icon: <Phone />,
                      },
                      {
                        label: (
                          <a
                            target="_blank"
                            rel="noopener"
                            href={
                              'http://maps.google.com/?q=' +
                              dealer?.address1 +
                              '+' +
                              dealer?.city +
                              '+' +
                              dealer?.stateProvince +
                              '+' +
                              dealer?.postalCode
                            }
                          >
                            <address>
                              {dealer?.address1 || ''}
                              {dealer?.address2 || ''}
                              <br />
                              {dealer?.city
                                ? dealer?.city +
                                  (dealer?.stateProvince
                                    ? ', ' + dealer?.stateProvince
                                    : '')
                                : ''}
                              {dealer.postalCode ? ' ' + dealer.postalCode : ''}
                            </address>
                          </a>
                        ),
                        icon: <Address />,
                      },
                    ]}
                  >
                    {/* <span
                      onClick={() => alert('contact modal will go here')}
                      style={{
                        marginTop: `16px`,
                        display: 'flex',
                        cursor: 'pointer',
                      }}
                    >
                      <Typography
                        variant="button"
                        color="#0075C9"
                        components={{ Root: 'span' }}
                      >
                        Send Message
                      </Typography>
                    </span> */}
                  </ContactCard>
                </div>
              </Stack>
            </div>
          ) : (
            <div className="loading-screen">
              <Loading width="90px" color="#0075c9" />
            </div>
          )}
        </AccountStyles>
      </ThemeProvider>
    </>
  );
};

export default Account;
